import { useState, useEffect } from "react";
import axios from "axios";
import {
  Users, RefreshCw, Plus, X, Mail, Building2, KeyRound,
  Power, Ban, Loader2, Search
} from "lucide-react";

const API = "https://api.komorotel.com/api/superadmin";

function ActivePill({ active, labels = ["Actif", "Inactif"] }) {
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
      active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
    }`}>
      {active ? labels[0] : labels[1]}
    </span>
  );
}

// ── Modal création admin (Flux 1) ──
function CreateAdminModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [hotelName, setHotelName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email) return setError("Email requis.");
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/admins`, { email, hotelName }, { withCredentials: true });
      onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la création.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] bg-white border border-[#e5e7eb] rounded-2xl z-50 p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-gray-900">Créer un compte hôtelier</p>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-400 transition">
            <X size={15} />
          </button>
        </div>

        <p className="text-xs text-gray-400 mb-4 leading-relaxed">
          Un email d'activation (valable 24h) sera envoyé. L'hôtelier choisira son identifiant
          et son mot de passe, puis créera son hôtel.
        </p>

        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Email de l'hôtelier *</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="hotelier@exemple.com"
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#e5e7eb] rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Nom de l'hôtel</label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={hotelName}
                onChange={e => setHotelName(e.target.value)}
                placeholder="Hôtel Karthala"
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#e5e7eb] rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
              />
            </div>
          </div>

          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-2.5 bg-[#3A9D23] text-white text-sm font-medium rounded-xl hover:bg-[#2d8019] transition disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Créer et envoyer l'invitation
          </button>
        </div>
      </div>
    </>
  );
}

export default function SuperAdminUsers() {
  const [tab, setTab] = useState("admins"); // admins | clients
  const [admins, setAdmins] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admins`, { withCredentials: true }),
      axios.get(`${API}/clients`, { withCredentials: true }),
    ])
      .then(([a, c]) => { setAdmins(a.data); setClients(c.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleToggleAdmin = async (admin) => {
    setActionLoading(admin._id);
    try {
      await axios.patch(`${API}/admins/${admin._id}/toggle`, {}, { withCredentials: true });
      setAdmins(list => list.map(a => a._id === admin._id ? { ...a, active: !a.active } : a));
    } catch (err) {
      alert(err.response?.data?.message || "Erreur.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (admin) => {
    if (!window.confirm(`Envoyer un email de réinitialisation de mot de passe à ${admin.email} ?`)) return;
    setActionLoading(admin._id);
    try {
      const r = await axios.post(`${API}/admins/${admin._id}/reset-password`, {}, { withCredentials: true });
      alert(r.data.message);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlockClient = async (client) => {
    const verb = client.blocked ? "débloquer" : "bloquer";
    if (!window.confirm(`Voulez-vous ${verb} ${client.username} ?`)) return;
    setActionLoading(client._id);
    try {
      await axios.patch(`${API}/clients/${client._id}/block`, {}, { withCredentials: true });
      setClients(list => list.map(c => c._id === client._id ? { ...c, blocked: !c.blocked } : c));
    } catch (err) {
      alert(err.response?.data?.message || "Erreur.");
    } finally {
      setActionLoading(null);
    }
  };

  const filteredAdmins = admins.filter(a =>
    (a.username || "").toLowerCase().includes(search.toLowerCase()) ||
    (a.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (a.hotelId?.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const filteredClients = clients.filter(c =>
    (c.username || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.email || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-full min-h-screen bg-[#f9fafb] p-6 flex flex-col gap-5">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Utilisateurs</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {admins.length} admin{admins.length > 1 ? "s" : ""} hôtelier{admins.length > 1 ? "s" : ""} · {clients.length} client{clients.length > 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            className="w-9 h-9 bg-white border border-[#e5e7eb] rounded-xl flex items-center justify-center hover:bg-gray-50 transition text-gray-400"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
          {tab === "admins" && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#3A9D23] text-white text-sm font-medium rounded-xl hover:bg-[#2d8019] transition"
            >
              <Plus size={15} />
              Créer un admin
            </button>
          )}
        </div>
      </div>

      {/* ── ONGLETS + RECHERCHE ── */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 bg-white border border-[#e5e7eb] rounded-xl p-1">
          {[
            { key: "admins", label: "Admins hôteliers" },
            { key: "clients", label: "Clients" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setSearch(""); }}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition duration-150 ${
                tab === t.key ? "bg-[#3A9D23] text-white" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
          />
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          {tab === "admins" ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e5e7eb] text-left">
                  {["Identifiant", "Email", "Hôtel associé", "Statut", "Créé le", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-gray-400">Chargement...</td></tr>
                ) : filteredAdmins.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-gray-400">Aucun admin trouvé.</td></tr>
                ) : filteredAdmins.map(a => (
                  <tr key={a._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition duration-150">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 whitespace-nowrap">
                        {a.username?.startsWith("pending_") ? <span className="text-gray-400 italic">En attente d'activation</span> : a.username}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{a.email || "—"}</td>
                    <td className="px-4 py-3">
                      {a.hotelId
                        ? <span className="text-gray-700 whitespace-nowrap">{a.hotelId.name} <span className="text-[11px] text-gray-400">({a.hotelId.island})</span></span>
                        : <span className="text-[11px] text-gray-400">Aucun hôtel</span>}
                    </td>
                    <td className="px-4 py-3"><ActivePill active={a.active !== false} /></td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(a.createdAt).toLocaleDateString("fr-FR")}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleResetPassword(a)}
                          disabled={actionLoading === a._id || !a.email}
                          title="Envoyer un email de réinitialisation"
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#e5e7eb] text-gray-500 text-xs hover:bg-gray-50 transition disabled:opacity-50"
                        >
                          <KeyRound size={12} /> Reset MDP
                        </button>
                        <button
                          onClick={() => handleToggleAdmin(a)}
                          disabled={actionLoading === a._id}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition disabled:opacity-50 ${
                            a.active !== false
                              ? "border border-[#e5e7eb] text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-100"
                              : "bg-[#3A9D23] text-white hover:bg-[#2d8019]"
                          }`}
                        >
                          {actionLoading === a._id ? <Loader2 size={12} className="animate-spin" /> : <Power size={12} />}
                          {a.active !== false ? "Désactiver" : "Activer"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e5e7eb] text-left">
                  {["Client", "Email", "Réservations", "Dernière activité", "Statut", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-gray-400">Chargement...</td></tr>
                ) : filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Users className="text-gray-300 mx-auto mb-2" size={24} />
                      <p className="text-xs text-gray-400">Aucun client trouvé.</p>
                    </td>
                  </tr>
                ) : filteredClients.map(c => (
                  <tr key={c._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition duration-150">
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{c.username}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{c.email}</td>
                    <td className="px-4 py-3 text-gray-600">{c.reservationsCount}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(c.lastActivity).toLocaleDateString("fr-FR")}
                    </td>
                    <td className="px-4 py-3">
                      <ActivePill active={!c.blocked} labels={["Actif", "Bloqué"]} />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleBlockClient(c)}
                        disabled={actionLoading === c._id}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition disabled:opacity-50 ${
                          c.blocked
                            ? "bg-[#3A9D23] text-white hover:bg-[#2d8019]"
                            : "border border-[#e5e7eb] text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-100"
                        }`}
                      >
                        {actionLoading === c._id ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                        {c.blocked ? "Débloquer" : "Bloquer"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate && <CreateAdminModal onClose={() => setShowCreate(false)} onCreated={fetchAll} />}
    </div>
  );
}
